import { useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux";

import {
  INITIAL_MATCH_FORM,
  INITIAL_GROUP_FORM,
  INITIAL_BRACKET_FORM,
  INITIAL_SECTION_FORM,
  INITIAL_BRACKET_GROUP_FORM,
  INITIAL_BRACKET_MATCH_FORM,
  INITIAL_RULE_FORM,
  INITIAL_EXECUTE_RULES_DATA,
} from "../constants";

const useFormatState = () => {
  const { selectedOrganisation } = useSelector((state) => state.organisation);
  const { selectedTournament, selectedCategory } = useSelector(
    (state) => state.tournament
  );

  // Core state
  const [phases, setPhases] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedCategoryLabel, setSelectedCategoryLabel] = useState("");
  const [openDropdownPhaseId, setOpenDropdownPhaseId] = useState(null);
  const [openEditDropdownPhaseId, setOpenEditDropdownPhaseId] = useState(null);
  const phaseDropdownRef = useRef(null);

  // Template selection
  const [selectedTemplate, setSelectedTemplate] = useState("manualGrid");

  // Phase modals
  const [isAddPhaseModalOpen, setIsAddPhaseModalOpen] = useState(false);
  const [isPhaseSettingsModalOpen, setIsPhaseSettingsModalOpen] = useState(false);
  const [isCreateFormatModalOpen, setIsCreateFormatModalOpen] = useState(false);
  const [isPhaseDeleteModalOpen, setIsPhaseDeleteModalOpen] = useState(false);
  const [phaseToDelete, setPhaseToDelete] = useState(null);
  const [editingPhase, setEditingPhase] = useState(null);
  const [phaseInitialData, setPhaseInitialData] = useState(null);

  // Match state
  const [isMatchModalOpen, setIsMatchModalOpen] = useState(false);
  const [isDeleteMatchModalOpen, setIsDeleteMatchModalOpen] = useState(false);
  const [matchMode, setMatchMode] = useState("create");
  const [selectedPhaseForMatch, setSelectedPhaseForMatch] = useState(null);
  const [selectedMatchGroup, setSelectedMatchGroup] = useState(null);
  const [matchFormData, setMatchFormData] = useState(INITIAL_MATCH_FORM);

  // Group state
  const [isGroupModalOpen, setIsGroupModalOpen] = useState(false);
  const [isDeleteGroupModalOpen, setIsDeleteGroupModalOpen] = useState(false);
  const [groupMode, setGroupMode] = useState("create");
  const [selectedPhaseForGroup, setSelectedPhaseForGroup] = useState(null);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [groupFormData, setGroupFormData] = useState(INITIAL_GROUP_FORM);

  // Bracket state
  const [isBracketModalOpen, setIsBracketModalOpen] = useState(false);
  const [isDeleteBracketModalOpen, setIsDeleteBracketModalOpen] = useState(false);
  const [bracketMode, setBracketMode] = useState("create");
  const [selectedPhaseForBracket, setSelectedPhaseForBracket] = useState(null);
  const [selectedBracket, setSelectedBracket] = useState(null);
  const [bracketFormData, setBracketFormData] = useState(INITIAL_BRACKET_FORM);

  // Section state
  const [isBracketSectionModalOpen, setIsBracketSectionModalOpen] = useState(false);
  const [selectedBracketForSection, setSelectedBracketForSection] = useState(null);
  const [sectionFormData, setSectionFormData] = useState(INITIAL_SECTION_FORM);

  // Bracket-group state
  const [isBracketGroupModalOpen, setIsBracketGroupModalOpen] = useState(false);
  const [selectedBracketForGroup, setSelectedBracketForGroup] = useState(null);
  const [bracketGroupFormData, setBracketGroupFormData] = useState(INITIAL_BRACKET_GROUP_FORM);

  // Bracket-match state
  const [isBracketMatchModalOpen, setIsBracketMatchModalOpen] = useState(false);
  const [selectedBracketForMatch, setSelectedBracketForMatch] = useState(null);
  const [bracketMatchFormData, setBracketMatchFormData] = useState(INITIAL_BRACKET_MATCH_FORM);

  // Team assignment state
  const [showTeamSelector, setShowTeamSelector] = useState(false);
  const [selectedTeamSlot, setSelectedTeamSlot] = useState(null);
  const [selectedPhaseId, setSelectedPhaseId] = useState(null);
  const [selectedBlockId, setSelectedBlockId] = useState(null);
  const teamSelectorRef = useRef(null);

  // Progression rules state
  const [progressionRulesMapping, setProgressionRulesMapping] = useState({});
  const [isCreateRuleModalOpen, setIsCreateRuleModalOpen] = useState(false);
  const [isEditRuleModalOpen, setIsEditRuleModalOpen] = useState(false);
  const [isExecuteRulesModalOpen, setIsExecuteRulesModalOpen] = useState(false);
  const [selectedPhaseForRule, setSelectedPhaseForRule] = useState(null);
  const [selectedRule, setSelectedRule] = useState(null);
  const [ruleFormData, setRuleFormData] = useState(INITIAL_RULE_FORM);
  const [executeRulesData, setExecuteRulesData] = useState(INITIAL_EXECUTE_RULES_DATA);

  // Extra points state
  const [isExtraPointsModalOpen, setIsExtraPointsModalOpen] = useState(false);
  const [selectedGroupForExtraPoints, setSelectedGroupForExtraPoints] = useState(null);
  const [extraPointsFormData, setExtraPointsFormData] = useState({
    teamId: null,
    extraPoints: 0,
  });

  // Sync phases from Redux
  useEffect(() => {
    if (selectedCategory?.phases) {
      setPhases(selectedCategory.phases);
    }
  }, [selectedCategory]);

  // Set default category
  useEffect(() => {
    if (selectedTournament?.categories?.length > 0 && !selectedCategoryLabel) {
      const defaultLabel = selectedCategory?.label || selectedTournament.categories[0].label;
      setSelectedCategoryLabel(defaultLabel);
    }
  }, [selectedTournament, selectedCategoryLabel, selectedCategory]);

  // Close dropdowns on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (phaseDropdownRef.current && !phaseDropdownRef.current.contains(event.target)) {
        setOpenDropdownPhaseId(null);
        setOpenEditDropdownPhaseId(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return {
    // Redux data
    selectedOrganisation,
    selectedTournament,
    selectedCategory,
    
    // Core state
    phases,
    setPhases,
    isLoading,
    setIsLoading,
    selectedCategoryLabel,
    setSelectedCategoryLabel,
    openDropdownPhaseId,
    setOpenDropdownPhaseId,
    openEditDropdownPhaseId,
    setOpenEditDropdownPhaseId,
    phaseDropdownRef,
    
    // Template selection
    selectedTemplate,
    setSelectedTemplate,

    // Phase modals
    isAddPhaseModalOpen,
    setIsAddPhaseModalOpen,
    isPhaseSettingsModalOpen,
    setIsPhaseSettingsModalOpen,
    isCreateFormatModalOpen,
    setIsCreateFormatModalOpen,
    isPhaseDeleteModalOpen,
    setIsPhaseDeleteModalOpen,
    phaseToDelete,
    setPhaseToDelete,
    editingPhase,
    setEditingPhase,
    phaseInitialData,
    setPhaseInitialData,
    
    // Match state
    isMatchModalOpen,
    setIsMatchModalOpen,
    isDeleteMatchModalOpen,
    setIsDeleteMatchModalOpen,
    matchMode,
    setMatchMode,
    selectedPhaseForMatch,
    setSelectedPhaseForMatch,
    selectedMatchGroup,
    setSelectedMatchGroup,
    matchFormData,
    setMatchFormData,
    
    // Group state
    isGroupModalOpen,
    setIsGroupModalOpen,
    isDeleteGroupModalOpen,
    setIsDeleteGroupModalOpen,
    groupMode,
    setGroupMode,
    selectedPhaseForGroup,
    setSelectedPhaseForGroup,
    selectedGroup,
    setSelectedGroup,
    groupFormData,
    setGroupFormData,
    
    // Bracket state
    isBracketModalOpen,
    setIsBracketModalOpen,
    isDeleteBracketModalOpen,
    setIsDeleteBracketModalOpen,
    bracketMode,
    setBracketMode,
    selectedPhaseForBracket,
    setSelectedPhaseForBracket,
    selectedBracket,
    setSelectedBracket,
    bracketFormData,
    setBracketFormData,
    
    // Section state
    isBracketSectionModalOpen,
    setIsBracketSectionModalOpen,
    selectedBracketForSection,
    setSelectedBracketForSection,
    sectionFormData,
    setSectionFormData,
    
    // Bracket-group state
    isBracketGroupModalOpen,
    setIsBracketGroupModalOpen,
    selectedBracketForGroup,
    setSelectedBracketForGroup,
    bracketGroupFormData,
    setBracketGroupFormData,
    
    // Bracket-match state
    isBracketMatchModalOpen,
    setIsBracketMatchModalOpen,
    selectedBracketForMatch,
    setSelectedBracketForMatch,
    bracketMatchFormData,
    setBracketMatchFormData,
    
    // Team assignment
    showTeamSelector,
    setShowTeamSelector,
    selectedTeamSlot,
    setSelectedTeamSlot,
    selectedPhaseId,
    setSelectedPhaseId,
    selectedBlockId,
    setSelectedBlockId,
    teamSelectorRef,
    
    // Progression rules
    progressionRulesMapping,
    setProgressionRulesMapping,
    isCreateRuleModalOpen,
    setIsCreateRuleModalOpen,
    isEditRuleModalOpen,
    setIsEditRuleModalOpen,
    isExecuteRulesModalOpen,
    setIsExecuteRulesModalOpen,
    selectedPhaseForRule,
    setSelectedPhaseForRule,
    selectedRule,
    setSelectedRule,
    ruleFormData,
    setRuleFormData,
    executeRulesData,
    setExecuteRulesData,
    
    // Extra points
    isExtraPointsModalOpen,
    setIsExtraPointsModalOpen,
    selectedGroupForExtraPoints,
    setSelectedGroupForExtraPoints,
    extraPointsFormData,
    setExtraPointsFormData,
  };
};

export default useFormatState;
